<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert as wgptUseAlert } from 'dashboard/composables/wgpt';
import { useAlert } from 'dashboard/composables';

import SnackbarContainer from 'next/wgpt/Snackbar/SnackbarContainer.vue';
import Dialog from 'next/dialog/Dialog.vue';
import EditStageForm from './EditStageForm.vue';
import { computed } from 'vue';

const emit = defineEmits(['close']);
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const dialogRef = ref(null);
const bot = ref({});
const workflow = ref({});
const stage = ref({});
const isOpen = ref(false);
const uiFlags = getters['wgptBotWorkflowStages/getUIFlags'];

const title = computed(() => {
  return (
    t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.STAGE.EDIT.TITLE') +
    ' ' +
    workflow.value.name
  );
});

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const formRef = ref(null);

const open = (selectedBot, selectedWorkflow, selectedStage) => {
  bot.value = selectedBot;
  workflow.value = selectedWorkflow;
  stage.value = selectedStage;
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate } = formRef.value;

    const data = await validate();
    if (!data) return;

    const stageUpdates = {
      ...data,
    };
    await store.dispatch('wgptBotWorkflowStages/update', {
      ...stageUpdates,
      botId: bot.value.id,
      workflowId: workflow.value.id,
      id: stage.value.id,
    });

    useAlert(t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.STAGE.EDIT.API.SUCCESS_MESSAGE'));
    close();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.STAGE.EDIT.API.ERROR_MESSAGE');
    wgptUseAlert(errorMessage);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :confirm-button-label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.STAGE.EDIT.SAVE')"
    :cancel-button-label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.STAGE.EDIT.CANCEL')"
    :is-loading="uiFlags.isCreating"
    :disable-confirm-button="uiFlags.isCreating"
    width="xl"
    @confirm="handleSubmit"
    @close="onClose"
  >
    <SnackbarContainer class="-top-6 fixed -translate-y-full" />
    <EditStageForm
      v-if="isOpen"
      ref="formRef"
      :stage="stage"
      @submit="handleSubmit"
    />
  </Dialog>
</template>
